import {
  Component,
  computed,
  DestroyRef,
  effect,
  ElementRef,
  inject,
  input,
  output,
  Renderer2,
  ViewChild
} from '@angular/core';
import { Router } from "@angular/router";
import { AuthService } from '@core/services/auth.service';
import { DocumentRecord } from "@models/annual-statements/document-record";
import { DocumentStatusEnum } from "@models/documents/document-status.enum";
import { NgbTooltip } from "@ng-bootstrap/ng-bootstrap";
import { DividerComponent } from "@shared/components/commons/divider/divider.component";
import { LoaderComponent } from "@shared/components/commons/loader/loader.component";
import { TooltipComponent } from "@shared/components/commons/tooltip/tooltip.component";
import {
  AgDocumentStatusComponent
} from "@shared/components/grid/ag-cells/common/ag-document-status/ag-document-status.component";
import { CheckboxComponent } from "@shared/controls/checkbox/checkbox.component";
import { ToggleButtonComponent } from "@shared/controls/toggle-button/toggle-button.component";
import { HasRoleDirective } from '@shared/directives/has-role.directive';
import { DocumentTypePipe } from '@shared/pipes/document-type.pipe';
import { DocumentsApiService } from "@shared/services/documents-api.service";
import { DocumentCommonService } from '@shared/services/documents-common.service';
import { DocumentsService } from "@shared/services/documents.service";
import { documentStatusToStep } from "@shared/utils/document-status-to-step";
import { getGenerateDocumentPath } from "@shared/utils/route-path-utils";
import { ToastrService } from "ngx-toastr";
import { catchError, combineLatest, map, Observable, of, timer } from "rxjs";

@Component({
  selector: 'app-entity-pending-changes',
  standalone: true,
  imports: [
    DividerComponent,
    ToggleButtonComponent,
    LoaderComponent,
    CheckboxComponent,
    NgbTooltip,
    DocumentTypePipe,
    TooltipComponent,
    HasRoleDirective,
    AgDocumentStatusComponent
  ],
  templateUrl: 'entity-pending-changes.component.html',
  styleUrl: 'entity-pending-changes.component.scss'
})
export class EntityPendingChangesComponent {
  @ViewChild('profileCard') private profileCardElRef!: ElementRef<HTMLLabelElement>;

  readonly documentCommonService = inject(DocumentCommonService);
  readonly authService = inject(AuthService);
  readonly documentsService = inject(DocumentsService);
  readonly documentsApiService = inject(DocumentsApiService);
  readonly router: Router = inject(Router);
  readonly renderer = inject(Renderer2);
  readonly toastr = inject(ToastrService);
  readonly destroyRef = inject(DestroyRef);

  isLoading = input<boolean>(false);
  pendingDocuments = input<DocumentRecord[]>([]);
  isCurrentChangesHighlighted = input<boolean>(false);
  trustView = input<boolean>(false);
  isPendingChangesVisible = input<boolean>(false);
  disableControls = input<boolean>(false);

  setIsPendingChangesVisible = output<boolean>();
  updatePendingDocuments = output();
  deletePendingDocuments = output<DocumentRecord>();
  openEditForm = output<DocumentRecord>();

  readonly DocumentStatusEnum = DocumentStatusEnum;
  selectedDocumentsIdsRecord: Record<string, boolean> = {};

  visibleDocumentRecords = computed(() => this.pendingDocuments().filter((documentRecord) => !DocumentRecord.isDocumentInvoice(documentRecord)));

  constructor() {
    effect(() => {
      this.selectedDocumentsIdsRecord = {};

      this.pendingDocuments().forEach((documentRecord) => {
        this.selectedDocumentsIdsRecord[documentRecord.documentName] = false;
      });
    });

    effect(() => this.onHighlightNewDocumentRecord(this.isCurrentChangesHighlighted()));
  }

  isAllowToDeleteDocument(documentRecord: DocumentRecord): boolean {
    return !this.documentCommonService.isAllowedToDeleteDocuments([documentRecord]);
  }

  onSwitchButton(state: boolean): void {
    this.setIsPendingChangesVisible.emit(state);
  }

  deleteChanges(documentRecord: DocumentRecord): void {
    this.deletePendingDocuments.emit(documentRecord);
  }

  generateDocuments(): void {
    const checkedPendingDocuments = this.visibleDocumentRecords().filter((documentRecord) => this.selectedDocumentsIdsRecord[documentRecord.documentId]);
    const documentIds = checkedPendingDocuments.map(({ documentId }) => documentId);
    const documentStatus = checkedPendingDocuments[0].status ?? 0;

    if (documentIds.length === 1) {
      this.openDocument(documentIds[0], documentStatus);
    } else if (documentIds.length > 1) {
      const isPossibleMerge = checkedPendingDocuments.every((documentRecord) => (documentRecord.type === 'c:484'));

      if (isPossibleMerge) {
        this.documentsApiService.mergeDocuments(documentIds)
          .pipe(
            catchError(() => {
              this.toastr.error('Documents can\'t be merged!');
              return of();
            }),
          )
          .subscribe({
            next: (response) => {
              if (response?.documentId) {
                this.openDocument(response.documentId, 0);
              }
            },
            error: (error) => {
              console.warn('[mergeDocuments] error: ', error);
              this.toastr.error('Failed to merge documents', 'Error');
            }
          });
      } else {
        this.toastr.error('Documents can\'t be merged!');
      }
    }
  }

  onHighlightNewDocumentRecord(isCurrentChangesHighlighted: boolean): void {
    this.endDocumentHighlightAnimation(0).subscribe();

    if (!isCurrentChangesHighlighted) return;

    const firstDocument: DocumentRecord | null = this.visibleDocumentRecords()[0] ?? null;

    if (firstDocument) {
      this.selectedDocumentsIdsRecord[firstDocument.documentId] = true;
      this.playDocumentHighlightAnimation();
    }
  }

  playDocumentHighlightAnimation(): void {
    combineLatest([
      this.startDocumentHighlightAnimation(),
      this.endDocumentHighlightAnimation()
    ])
      .subscribe();
  }

  startDocumentHighlightAnimation(delay = 500): Observable<void> {
    return timer(delay)
      .pipe(
        map(() => {
          if (this.profileCardElRef.nativeElement) {
            this.renderer.addClass(this.profileCardElRef.nativeElement, 'grow');
            this.renderer.addClass(this.profileCardElRef.nativeElement, 'highlight-border');
          }
        }),
      );
  }

  endDocumentHighlightAnimation(delay = 1500): Observable<void> {
    return timer(delay)
      .pipe(
        map(() => {
          this.renderer.removeClass(this.profileCardElRef.nativeElement, 'grow');
          // this.renderer.removeClass(this.profileCardElRef.nativeElement, 'highlight-border');
        })
      );
  }

  checkRolesAndOpen(document: DocumentRecord): void {
    const allowedRoles = ['Admin', 'Manager', 'Preparer'];
    const canBeOpened = this.authService.hasRole(allowedRoles);

    if (!canBeOpened) return;

    if (document.status !== DocumentStatusEnum.Draft) {
      this.openDocument(document.documentId, document.status);
    } else {
      this.onOpenEditForm(document);
    }
  }

  openDocument(documentId: string, documentStatus: DocumentStatusEnum | undefined): void {
    const step = documentStatusToStep(documentStatus);
    void this.router.navigate(getGenerateDocumentPath(documentId), { queryParams: { step } });
  }

  onOpenEditForm(document: DocumentRecord): void {
    this.openEditForm.emit(document);
  }

  get isAllNotChecked(): boolean {
    return Object.values(this.selectedDocumentsIdsRecord).every((selected) => !selected);
  }

  get isOneMoreChecked(): boolean {
    return Object.values(this.selectedDocumentsIdsRecord).some((selected) => selected);
  }

  get isFirst484FormSelected(): boolean {
    const firstDocument: DocumentRecord | null = this.visibleDocumentRecords()[0] ?? null;

    if (!firstDocument || !this.isCurrentChangesHighlighted()) return false;

    const isFirstDocument484 = this.visibleDocumentRecords()[0].type.includes('484');
    const isFirstDocumentChecked = this.selectedDocumentsIdsRecord[firstDocument.documentId];

    return isFirstDocument484 && isFirstDocumentChecked;
  }
}
